import { FC, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { MdClose, MdBugReport, MdLockOpen, MdContentCopy, MdCheck, MdPushPin, MdOutlinePushPin, MdEmergency, MdRemoveCircleOutline, MdLockOutline, MdEdit, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useFloating, offset, flip, shift, size, getOverflowAncestors, autoUpdate, useDismiss, useInteractions, useRole, FloatingPortal, type Middleware, type ReferenceElement } from '@floating-ui/react';
import Tooltip from '@srm/ui/tooltip';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useDebugPanel, type FieldOverrideProp } from '@/context/DebugPanelContext';
import type { IField } from '@models/IFormData';
import { copyWithToast } from '../utils/debugPanelUtils';
import { useDirectWrite } from '../FormInspector/useDirectWrite';
import RequisiteEditor from './RequisiteEditor';
import RelatedRequisites from './RelatedRequisites';
import { getFieldDisplayName, getRelatedRequisites } from './fieldEditorUtils';
import './FieldFormulaTooltip.scss';

const INSPECTOR_OPEN_EVENT = 'field-inspector-open';

const getInspectorPanel = (reference: ReferenceElement) => reference instanceof HTMLElement
    ? reference.closest<HTMLElement>('.document-tabs--primary > .rf-tabs__content > [role="tabpanel"]')
        ?? reference.closest<HTMLElement>('[role="tabpanel"]')
    : null;

const getInspectorBoundary = (reference: ReferenceElement, pinned: boolean) => {
    const panel = !pinned && getInspectorPanel(reference);
    return panel
        ? [panel, ...getOverflowAncestors(panel).filter((element): element is HTMLElement => element instanceof HTMLElement)]
        : 'clippingAncestors' as const;
};

interface IFieldFormulaTooltipProps {
    required?: string;
    visibilityRules?: string;
    /** Формула заголовка (блок / вкладка). */
    nameFormula?: string;
    readonly?: string;
    min?: string;
    max?: string;
    pattern?: string;
    maxLength?: number | null;
    workCalendarRules?: string;
    fieldKey?: string;
    /** Поле сейчас рендерится в режиме просмотра — запись в форму и оверрайды недоступны. */
    isViewMode?: boolean;
    /** docId для прямой записи; по умолчанию — id открытого документа из стора. */
    docId?: string;
    /** RHF-путь `fields.[N].value` основного реквизита. Его наличие включает «богатый» жучок. */
    rhfPath?: string | null;
    /** datatype основного реквизита для парсинга ввода. */
    datatype?: string;
    /** Текущее значение основного реквизита. */
    value?: IField['value'];
    /** Результаты формул (без учёта оверрайда) — для строки «→ сейчас». */
    requiredResult?: boolean;
    readonlyResult?: boolean;
    visibilityResult?: boolean;
    minResult?: string;
    maxResult?: string;
    /** Включить блок оверрайдов правил без редактора значения (для колонок таблиц). */
    enableOverrides?: boolean;
}

interface IBoolRule {
    name: string;
    prop: Extract<FieldOverrideProp, 'required' | 'readonly' | 'visibility'>;
    formula?: string;
    result?: boolean;
    on: string;
    off: string;
    effTrue: string;
    effFalse: string;
}

const RULE_STATES = {
    required: {
        on: { icon: MdEmergency, tone: 'info' },
        off: { icon: MdRemoveCircleOutline, tone: 'neutral' },
    },
    readonly: {
        on: { icon: MdLockOutline, tone: 'warning' },
        off: { icon: MdEdit, tone: 'success' },
    },
    visibility: {
        on: { icon: MdVisibility, tone: 'info' },
        off: { icon: MdVisibilityOff, tone: 'neutral' },
    },
} as const;

interface IValueRule {
    prop: Extract<FieldOverrideProp, 'min' | 'max' | 'maxLength' | 'pattern'>;
    formula?: string;
    result?: string | number;
    inputType: 'text' | 'number';
}

const FormulaCode: FC<{ text: string; hint?: string }> = ({ text, hint = text }) => (
    <Tooltip
        wrapTarget={false}
        interactive={false}
        position="top-start"
        zIndex={1200}
        maxWidth="min(480px, calc(100vw - 24px))"
        label={<code className="rf-field-formula-tooltip__formula-hint">{hint}</code>}
    >
        <button type="button" className="rf-field-formula-tooltip__ovr-formula" aria-label={`Формула: ${hint}`}>{text}</button>
    </Tooltip>
);

/**
 * Строка оверрайда правила-значения (min/max/maxLength/pattern). Ввод копится в draft и применяется
 * по кнопке-галочке (или Enter), а не на каждый символ — иначе форма перевалидируется на каждое нажатие.
 */
const ValueOverrideRow: FC<{
    rule: IValueRule;
    cur: string | number | undefined;
    isViewMode?: boolean;
    onApply: (value: string | number | null) => void;
}> = ({ rule, cur, isViewMode, onApply }) => {
    const applied = cur != null ? String(cur) : '';
    const [draft, setDraft] = useState(applied);
    // Синхронизируем draft, когда применённое значение изменилось извне (сброс «По формуле», reload).
    const lastApplied = useRef(applied);
    useEffect(() => {
        if (lastApplied.current !== applied) {
            lastApplied.current = applied;
            setDraft(applied);
        }
    }, [applied]);

    const overridden = cur !== undefined;
    const eff = cur !== undefined ? cur : rule.result;
    const dirty = draft !== applied;

    const apply = () => {
        if (isViewMode || !dirty) return;
        if (draft === '') { onApply(null); return; }
        if (rule.inputType === 'number') {
            const n = Number(draft);
            if (!Number.isNaN(n)) onApply(n);
        } else {
            onApply(draft);
        }
    };

    return (
        <div className="rf-field-formula-tooltip__ovr-row">
            <div className="rf-field-formula-tooltip__ovr-head">
                <span className="rf-field-formula-tooltip__ovr-label">{rule.prop}</span>
                {rule.formula && <FormulaCode text={rule.formula} />}
                {eff != null && eff !== '' && <span className="rf-field-formula-tooltip__now">→ <b>{String(eff)}</b></span>}
            </div>
            <div className="rf-field-formula-tooltip__ovr-value">
                <button
                    type="button"
                    className={`rf-field-formula-tooltip__tri-btn ${!overridden ? 'is-active' : ''}`}
                    aria-pressed={!overridden}
                    disabled={isViewMode}
                    onClick={() => onApply(null)}
                >
                    По формуле
                </button>
                <input
                    className={`rf-field-formula-tooltip__ovr-input ${overridden ? 'is-overridden' : ''}`}
                    type={rule.inputType}
                    value={draft}
                    placeholder={rule.result != null ? String(rule.result) : 'значение…'}
                    disabled={isViewMode}
                    onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') apply(); if (e.key === 'Escape') setDraft(applied); }}
                    onChange={(e) => setDraft(e.target.value)}
                    aria-label={`Оверрайд ${rule.prop}`}
                />
                <button
                    type="button"
                    className="rf-field-formula-tooltip__ovr-apply"
                    disabled={isViewMode || !dirty}
                    onClick={apply}
                    title={draft === '' && overridden ? 'Сбросить оверрайд' : 'Применить оверрайд'}
                    aria-label={`Применить оверрайд ${rule.prop}`}
                >
                    <MdCheck size={13} />
                </button>
            </div>
        </div>
    );
};

const FieldFormulaTooltip: FC<IFieldFormulaTooltipProps> = ({
    required,
    visibilityRules,
    nameFormula,
    readonly,
    min,
    max,
    pattern,
    maxLength,
    workCalendarRules,
    fieldKey,
    isViewMode,
    docId: docIdProp,
    rhfPath,
    datatype,
    value,
    requiredResult,
    readonlyResult,
    visibilityResult,
    minResult,
    maxResult,
    enableOverrides,
}) => {
    const { fieldOverrides, setFieldOverride, toggleForcedEditable, fieldsData } = useDebugPanel();
    const scheme = useTypedSelector((state) => state.document.scheme);
    const storeDocId = useTypedSelector((state) => state.document.data?.id);
    const docId = docIdProp ?? storeDocId;
    const onDirectWrite = useDirectWrite();

    const ov = fieldKey ? fieldOverrides.get(fieldKey) : undefined;
    const anyOverride = !!ov && Object.keys(ov).length > 0;
    const isForced = ov?.readonly === false; // легаси-«снять readonly»
    const roToggleDisabled = !fieldKey || !!isViewMode;

    const isEditMode = !isViewMode;
    const directAvailable = !!docId && docId !== '-1';
    // «Богатый» жучок (единый раздел оверрайдов правил) — контролы-поля (rhfPath) и колонки таблиц
    // (enableOverrides). Block и ячейки таблиц-вью — легаси. Редактор значения — только там, где есть
    // одиночное значение (rhfPath); у колонки значения нет (оно у каждой строки своё).
    const rich = rhfPath !== undefined || !!enableOverrides;
    const valueEditable = rhfPath !== undefined && !!fieldKey && (directAvailable || isEditMode);

    const [open, setOpen] = useState(false);
    const [pinned, setPinned] = useState(false);
    const inspectorId = useId();
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const dragStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
    const initialPosition = useRef<{ x: number; y: number; referenceX: number; referenceY: number } | null>(null);
    const pinnedPosition = useRef<{ x: number; y: number } | null>(null);

    const announceOpen = useCallback(() => {
        document.dispatchEvent(new CustomEvent(INSPECTOR_OPEN_EVENT, { detail: inspectorId }));
    }, [inspectorId]);

    const changeOpen = useCallback((nextOpen: boolean) => {
        if (nextOpen) {
            announceOpen();
            setDragOffset({ x: 0, y: 0 });
            initialPosition.current = null;
            pinnedPosition.current = null;
        }
        setPinned(false);
        dragStart.current = null;
        setOpen(nextOpen);
    }, [announceOpen]);

    // Экземпляры есть и в полях, и в таблицах, иногда под разными провайдерами отладки.
    useEffect(() => {
        if (!open || pinned) return;
        const closePrevious = (event: Event) => {
            if ((event as CustomEvent<string>).detail !== inspectorId) changeOpen(false);
        };
        document.addEventListener(INSPECTOR_OPEN_EVENT, closePrevious);
        return () => document.removeEventListener(INSPECTOR_OPEN_EVENT, closePrevious);
    }, [open, pinned, inspectorId, changeOpen]);

    const { refs, floatingStyles, middlewareData, context } = useFloating({
        open,
        onOpenChange: changeOpen,
        strategy: 'fixed',
        placement: 'bottom-end',
        middleware: [
            offset(4),
            ...[
                flip(({ elements }) => ({ boundary: getInspectorBoundary(elements.reference, pinned), padding: 8 })),
                shift(({ elements }) => ({ boundary: getInspectorBoundary(elements.reference, pinned), padding: 8, crossAxis: true })),
                size(({ elements }) => ({
                    boundary: getInspectorBoundary(elements.reference, pinned),
                    padding: 8,
                    apply({ availableWidth, availableHeight, elements }) {
                        Object.assign(elements.floating.style, {
                            maxWidth: `${Math.max(0, availableWidth)}px`,
                            minWidth: `${Math.min(320, Math.max(0, availableWidth))}px`,
                            maxHeight: `${Math.max(0, availableHeight)}px`,
                        });
                    },
                })),
            ].map((middleware): Middleware => ({
                ...middleware,
                // Выбираем сторону и размеры один раз; при скролле окно уходит вместе с полем.
                fn: (state) => initialPosition.current ? {} : middleware.fn(state),
            })),
            {
                name: 'inspectorDrag',
                options: { dragOffset, pinned },
                async fn(state) {
                    initialPosition.current ??= {
                        x: state.x, y: state.y,
                        referenceX: state.rects.reference.x, referenceY: state.rects.reference.y,
                    };
                    const initial = initialPosition.current;
                    const reference = state.elements.reference;
                    const base = pinned && pinnedPosition.current
                        ? pinnedPosition.current
                        : {
                            x: initial.x + state.rects.reference.x - initial.referenceX,
                            y: initial.y + state.rects.reference.y - initial.referenceY,
                        };
                    const x = base.x + dragOffset.x;
                    const y = base.y + dragOffset.y;
                    if (pinned || !getInspectorPanel(reference)) return { x, y, data: { clipPath: undefined } };
                    const bounds = await state.platform.getClippingRect({
                        element: state.elements.floating,
                        boundary: getInspectorBoundary(reference, false),
                        rootBoundary: 'viewport',
                        strategy: state.strategy,
                    });
                    return {
                        x, y,
                        data: {
                            clipPath: `inset(${bounds.y - y}px ${x + state.rects.floating.width - bounds.x - bounds.width}px ${y + state.rects.floating.height - bounds.y - bounds.height}px ${bounds.x - x}px)`,
                        },
                    };
                },
            },
        ],
        whileElementsMounted: autoUpdate,
    });
    const dismiss = useDismiss(context, { outsidePress: !pinned });
    const role = useRole(context, { role: 'dialog' });
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);
    const setReference = useCallback((element: HTMLButtonElement | null) => refs.setReference(element), [refs]);
    const setFloating = useCallback((element: HTMLDivElement | null) => refs.setFloating(element), [refs]);

    useEffect(() => {
        const reference = refs.domReference.current;
        if (!open || pinned || !reference) return;
        const observer = new MutationObserver(() => {
            if (reference.closest('[aria-hidden="true"], [hidden], .rf-tabs__content_hide')) changeOpen(false);
        });
        let panel = reference.closest('[role="tabpanel"]');
        while (panel) {
            observer.observe(panel, { attributes: true, attributeFilter: ['aria-hidden', 'hidden', 'class'] });
            panel = panel.parentElement?.closest('[role="tabpanel"]') ?? null;
        }
        return () => observer.disconnect();
    }, [open, pinned, refs.domReference, changeOpen]);

    const related = useMemo(
        () => (open && rich ? getRelatedRequisites(scheme, fieldKey, fieldsData) : []),
        [open, rich, scheme, fieldKey, fieldsData],
    );
    const displayName = useMemo(() => open ? getFieldDisplayName(scheme, fieldKey) : '', [open, scheme, fieldKey]);

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        changeOpen(!open);
    }, [open, changeOpen]);

    const handlePin = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (pinned) {
            pinnedPosition.current = null;
            announceOpen();
        } else {
            const rect = refs.floating.current?.getBoundingClientRect();
            if (rect) pinnedPosition.current = { x: rect.x - dragOffset.x, y: rect.y - dragOffset.y };
        }
        setPinned((prev) => !prev);
    }, [pinned, announceOpen, refs.floating, dragOffset]);

    const onPointerDown = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.rf-field-formula-tooltip__header') || target.closest('button')) return;
            e.preventDefault();
            dragStart.current = { x: e.clientX, y: e.clientY, ox: dragOffset.x, oy: dragOffset.y };
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        },
        [dragOffset],
    );

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragStart.current) return;
        setDragOffset({
            x: dragStart.current.ox + e.clientX - dragStart.current.x,
            y: dragStart.current.oy + e.clientY - dragStart.current.y,
        });
    }, []);

    const onPointerUp = useCallback(() => {
        dragStart.current = null;
    }, []);

    const handleToggleForceEdit = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            if (roToggleDisabled || !fieldKey) return;
            toggleForcedEditable(fieldKey);
        },
        [roToggleDisabled, toggleForcedEditable, fieldKey],
    );

    // Легаси-таблица правил (ячейки таблиц / Block): без tri-state, только формулы + тумблер readonly.
    const legacyRuleRows = [
        { label: 'required', value: required },
        { label: 'visibility', value: visibilityRules },
        { label: 'name', value: nameFormula },
        { label: 'readonly', value: readonly },
        { label: 'min', value: min },
        { label: 'max', value: max },
        { label: 'pattern', value: pattern },
        { label: 'maxLength', value: maxLength != null ? String(maxLength) : undefined },
        { label: 'workCalendar', value: workCalendarRules },
    ].filter((r) => r.value);

    // Богатый жучок: булевы правила показываем всегда (их можно форсировать и без формулы),
    // значимые правила — только при наличии формулы/базы.
    const boolRules: IBoolRule[] = [
        { prop: 'required', name: 'Обязательность', formula: required, result: requiredResult, on: 'Обязательно', off: 'Не обязательно', effTrue: 'Обязательно', effFalse: 'Необязательно' },
        { prop: 'readonly', name: 'Редактирование', formula: readonly, result: readonlyResult, on: 'Только чтение', off: 'Можно менять', effTrue: 'Только чтение', effFalse: 'Доступно' },
        { prop: 'visibility', name: 'Видимость', formula: visibilityRules, result: visibilityResult, on: 'Показать', off: 'Скрыть', effTrue: 'Видно', effFalse: 'Скрыто' },
    ];
    const valueRulesAll: IValueRule[] = [
        { prop: 'min', formula: min, result: minResult, inputType: 'text' },
        { prop: 'max', formula: max, result: maxResult, inputType: 'text' },
        {
            prop: 'maxLength',
            formula: maxLength != null ? String(maxLength) : undefined,
            result: maxLength ?? undefined,
            inputType: 'number',
        },
        { prop: 'pattern', formula: pattern, result: pattern, inputType: 'text' },
    ];
    const valueRules = valueRulesAll.filter((r) => r.formula != null && r.formula !== '');

    const hasAnything = rich ? (valueEditable || boolRules.length > 0) : legacyRuleRows.length > 0;
    if (!hasAnything) return null;

    const triggerClassName = `rf-field-formula-tooltip__trigger${anyOverride ? ' rf-field-formula-tooltip__trigger--force-edit-active' : ''}`;
    const triggerTitle = anyOverride ? 'Реквизит поля (есть активные оверрайды)' : 'Реквизит поля: формулы и запись';
    const roBtnClassName = `rf-field-formula-tooltip__force-edit-btn${isForced ? ' rf-field-formula-tooltip__force-edit-btn--active' : ''}`;
    const roBtnTitle = roToggleDisabled
        ? isViewMode ? 'Недоступно в режиме просмотра' : 'Нет ключа поля'
        : isForced ? 'Вернуть readonly по формуле' : 'Игнорировать формулу readonly для этого поля';

    const setOv = (prop: FieldOverrideProp, value: boolean | string | number | null) => {
        if (fieldKey) setFieldOverride(fieldKey, prop, value);
    };

    return (
        <>
            <button
                ref={setReference}
                {...getReferenceProps()}
                type="button"
                className={triggerClassName}
                onClick={handleToggle}
                title={triggerTitle}
                aria-label={`Инспектор поля ${fieldKey ?? ''}`.trim()}
            >
                <MdBugReport size={14} />
            </button>

            {open && (
                <FloatingPortal>
                    <div
                        ref={setFloating}
                        {...getFloatingProps()}
                        aria-label={`Инспектор поля ${fieldKey ?? ''}`.trim()}
                        className="rf-field-formula-tooltip__popover"
                        style={{ ...floatingStyles, clipPath: middlewareData.inspectorDrag?.clipPath }}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                    >
                        <div className="rf-field-formula-tooltip__header">
                            <div className="rf-field-formula-tooltip__identity">
                                <div className="rf-field-formula-tooltip__field-name" title={displayName}>{displayName}</div>
                                <div className="rf-field-formula-tooltip__meta">
                                    {rich && (
                                        <span className={`rf-field-formula-tooltip__mode ${isViewMode ? 'rf-field-formula-tooltip__mode--view' : 'rf-field-formula-tooltip__mode--edit'}`}>
                                            {isViewMode ? 'Просмотр' : 'Редактирование'}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {fieldKey && (
                                <button
                                    type="button"
                                    className="rf-field-formula-tooltip__copy"
                                    aria-label="Копировать ключ поля"
                                    onClick={(e) => { e.stopPropagation(); copyWithToast(fieldKey, 'Реквизит'); }}
                                    title="Копировать реквизит"
                                >
                                    <MdContentCopy size={16} />
                                </button>
                            )}
                            <button
                                type="button"
                                className={`rf-field-formula-tooltip__pin${pinned ? ' is-active' : ''}`}
                                aria-label={pinned ? 'Открепить инспектор' : 'Закрепить инспектор'}
                                aria-pressed={pinned}
                                title={pinned ? 'Открепить инспектор' : 'Закрепить: не закрывать по клику снаружи и при открытии другого'}
                                onClick={handlePin}
                            >
                                {pinned ? <MdPushPin size={14} /> : <MdOutlinePushPin size={14} />}
                            </button>
                            <button
                                type="button"
                                className="rf-field-formula-tooltip__close"
                                aria-label="Закрыть"
                                onClick={handleToggle}
                            >
                                <MdClose size={14} />
                            </button>
                            {fieldKey && <code className="rf-field-formula-tooltip__field-key" title={fieldKey}>{fieldKey}</code>}
                        </div>

                        {valueEditable && (
                            <div className="rf-field-formula-tooltip__section">
                                <RequisiteEditor
                                    fieldKey={fieldKey}
                                    docId={docId ?? '-1'}
                                    rhfPath={rhfPath ?? null}
                                    datatype={datatype}
                                    value={value ?? null}
                                    isEditMode={isEditMode}
                                    directAvailable={directAvailable}
                                    onDirectWrite={onDirectWrite}
                                />
                            </div>
                        )}

                        {rich && fieldKey && (
                            <div className="rf-field-formula-tooltip__section">
                                <div className="rf-field-formula-tooltip__section-title">
                                    Правила поля
                                </div>

                                {boolRules.map((r) => {
                                    const cur = ov?.[r.prop];
                                    const eff = cur !== undefined ? cur : r.result;
                                    const state = RULE_STATES[r.prop][eff ? 'on' : 'off'];
                                    const StateIcon = state.icon;
                                    return (
                                        <div key={r.prop} className="rf-field-formula-tooltip__ovr-row">
                                            <div className="rf-field-formula-tooltip__ovr-head">
                                                <span className="rf-field-formula-tooltip__ovr-name">{r.name}</span>
                                                <FormulaCode text={`${r.prop}${r.formula ? `: ${r.formula}` : ''}`} hint={`${r.prop}: ${r.formula || 'По умолчанию'}`} />
                                                {eff !== undefined && (
                                                    <span className={`rf-field-formula-tooltip__now rf-field-formula-tooltip__state rf-field-formula-tooltip__state--${state.tone}`} title={cur !== undefined ? 'Задано вручную' : 'Результат правила'}>
                                                        <StateIcon size={13} aria-hidden />
                                                        <b>{eff ? r.effTrue : r.effFalse}</b>
                                                    </span>
                                                )}
                                            </div>
                                            <div className={`rf-field-formula-tooltip__tri ${isViewMode ? 'is-disabled' : ''}`} role="group" aria-label={`Оверрайд ${r.prop}`}>
                                                <button type="button" className={`rf-field-formula-tooltip__tri-btn ${cur === undefined ? 'is-active' : ''}`} aria-pressed={cur === undefined} disabled={isViewMode} onClick={() => setOv(r.prop, null)}>По формуле</button>
                                                <button type="button" className={`rf-field-formula-tooltip__tri-btn rf-field-formula-tooltip__tri-btn--override ${cur === true ? 'is-active' : ''}`} aria-pressed={cur === true} disabled={isViewMode} onClick={() => setOv(r.prop, true)}>{r.on}</button>
                                                <button type="button" className={`rf-field-formula-tooltip__tri-btn rf-field-formula-tooltip__tri-btn--override ${cur === false ? 'is-active' : ''}`} aria-pressed={cur === false} disabled={isViewMode} onClick={() => setOv(r.prop, false)}>{r.off}</button>
                                            </div>
                                        </div>
                                    );
                                })}

                                {valueRules.map((r) => (
                                    <ValueOverrideRow
                                        key={r.prop}
                                        rule={r}
                                        cur={ov?.[r.prop]}
                                        isViewMode={isViewMode}
                                        onApply={(v) => setOv(r.prop, v)}
                                    />
                                ))}

                                {workCalendarRules && (
                                    <div className="rf-field-formula-tooltip__ovr-row">
                                        <div className="rf-field-formula-tooltip__ovr-head">
                                            <span className="rf-field-formula-tooltip__ovr-label">workCalendar</span>
                                            <FormulaCode text={workCalendarRules} />
                                        </div>
                                    </div>
                                )}

                                {isViewMode && <div className="rf-field-formula-tooltip__note">Оверрайды правил работают только в режиме редактирования</div>}
                            </div>
                        )}

                        {!rich && legacyRuleRows.length > 0 && (
                            <div className="rf-field-formula-tooltip__section">
                                <div className="rf-field-formula-tooltip__section-title">
                                    Правила
                                    <button
                                        type="button"
                                        className={roBtnClassName}
                                        onClick={handleToggleForceEdit}
                                        disabled={roToggleDisabled}
                                        title={roBtnTitle}
                                    >
                                        <MdLockOpen size={12} />
                                        <span>{isForced ? 'Readonly снят' : 'Снять readonly'}</span>
                                    </button>
                                </div>
                                <table className="rf-field-formula-tooltip__table">
                                    <tbody>
                                        {legacyRuleRows.map((r) => (
                                            <tr key={r.label}>
                                                <td className="rf-field-formula-tooltip__label">{r.label}</td>
                                                <td className="rf-field-formula-tooltip__value">
                                                    {r.value}
                                                    {r.label === 'readonly' && isForced && (
                                                        <span className="rf-field-formula-tooltip__overridden-badge">overridden</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <RelatedRequisites
                            items={related}
                            docId={docId ?? '-1'}
                            isEditMode={isEditMode}
                            directAvailable={directAvailable}
                            onDirectWrite={onDirectWrite}
                        />
                    </div>
                </FloatingPortal>
            )}
        </>
    );
};

export default FieldFormulaTooltip;
